import React, { useState, useEffect, useMemo } from 'react';
import { createClient } from '@supabase/supabase-js';
import './ResetPassword.css';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

function parseQuery() {
  const p = new URLSearchParams(window.location.search);
  return {
    email: p.get('email') || '',
    token: p.get('token') || '',
  };
}

interface PasswordValidation {
  isValid: boolean;
  error: string;
}

const validatePassword = (password: string): PasswordValidation => {
  if (password.length < 8) {
    return { isValid: false, error: 'Password must be at least 8 characters' };
  }
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one uppercase letter' };
  }
  if (!/[a-z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one lowercase letter' };
  }
  if (!/[0-9]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one number' };
  }
  return { isValid: true, error: '' };
};

const ResetPassword: React.FC = () => {
  const queryParams = useMemo(parseQuery, []);

  const [email, setEmail] = useState(queryParams.email);
  const [token, setToken] = useState(queryParams.token);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validate password on change
  useEffect(() => {
    if (newPassword) {
      const { error } = validatePassword(newPassword);
      setPasswordError(error);
    } else {
      setPasswordError('');
    }
  }, [newPassword]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear any previous status
    setStatusMessage(null);

    // Trim inputs
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedToken = token.trim();
    const trimmedNewPassword = newPassword.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    // Validate empty fields
    if (!trimmedEmail) {
      setStatusMessage({ type: 'error', message: 'Please enter your email address' });
      return;
    }
    if (!trimmedToken) {
      setStatusMessage({ type: 'error', message: 'Please enter the reset code from your email' });
      return;
    }
    if (!trimmedNewPassword) {
      setStatusMessage({ type: 'error', message: 'Please enter a new password' });
      return;
    }
    if (!trimmedConfirmPassword) {
      setStatusMessage({ type: 'error', message: 'Please confirm your new password' });
      return;
    }

    // Validate password strength
    const { isValid, error } = validatePassword(trimmedNewPassword);
    if (!isValid) {
      setStatusMessage({ type: 'error', message: error });
      return;
    }

    // Validate passwords match
    if (trimmedNewPassword !== trimmedConfirmPassword) {
      setStatusMessage({ type: 'error', message: 'New password and confirmation must match' });
      return;
    }

    try {
      setIsSubmitting(true);

      // Verify OTP with Supabase
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: trimmedEmail,
        token: trimmedToken,
        type: 'recovery',
      });

      if (verifyError) {
        console.error('[ResetPassword] OTP verification error:', verifyError);
        setStatusMessage({ type: 'error', message: verifyError.message || 'Invalid or expired reset code' });
        setIsSubmitting(false);
        return;
      }

      // Now update the password (user is now authenticated after verifyOtp)
      const { error: updateError } = await supabase.auth.updateUser({
        password: trimmedNewPassword,
      });

      if (updateError) {
        console.error('[ResetPassword] Password update error:', updateError);
        setStatusMessage({ type: 'error', message: updateError.message || 'Failed to update password' });
        setIsSubmitting(false);
        return;
      }

      // Sign out after password reset so user can log in fresh
      await supabase.auth.signOut();

      setStatusMessage({
        type: 'success',
        message: 'Your password has been reset successfully! You can now sign in with your new password in the Fluffer app.',
      });
      setIsSubmitting(false);
    } catch (error: unknown) {
      console.error('[ResetPassword] Exception:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password. Please try again.';
      setStatusMessage({ type: 'error', message: errorMessage });
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    email.trim() &&
    token.trim() &&
    newPassword.trim() &&
    confirmPassword.trim() &&
    !passwordError &&
    newPassword === confirmPassword;

  return (
    <div className="reset-password fluffer-theme">
      <header className="reset-header">
        <div className="logo"><img src="/fluffer.png" alt="Fluffer" /></div>
      </header>

      <div className="reset-card">
        <h1 className="reset-title">Reset Password</h1>
        <p className="reset-subtitle">
          Enter the reset code from your email and choose a new password
        </p>

        {statusMessage && (
          <div className={`status-message ${statusMessage.type}`}>
            {statusMessage.type === 'success' && <span className="status-icon">&#10003;</span>}
            {statusMessage.type === 'error' && <span className="status-icon">&#10007;</span>}
            {statusMessage.message}
          </div>
        )}

        {statusMessage?.type !== 'success' && (
          <form onSubmit={handleResetPassword} className="reset-form">
            <div className="input-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                autoComplete="email"
                disabled={isSubmitting}
              />
            </div>

            <div className="input-group">
              <label htmlFor="token">Reset Code</label>
              <input
                id="token"
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Enter 6-digit code"
                autoComplete="one-time-code"
                disabled={isSubmitting}
              />
            </div>

            <div className="input-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="password-input-wrapper">
                <input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  tabIndex={-1}
                >
                  {showNewPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {passwordError && <span className="field-error">{passwordError}</span>}
            </div>

            <div className="input-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="password-input-wrapper">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <span className="field-error">Passwords do not match</span>
              )}
            </div>

            <div className="requirements-box">
              <p className="requirements-title">Password must contain:</p>
              <ul className="requirements-list">
                <li className={newPassword.length >= 8 ? 'met' : ''}>At least 8 characters</li>
                <li className={/[A-Z]/.test(newPassword) ? 'met' : ''}>At least one uppercase letter (A-Z)</li>
                <li className={/[a-z]/.test(newPassword) ? 'met' : ''}>At least one lowercase letter (a-z)</li>
                <li className={/[0-9]/.test(newPassword) ? 'met' : ''}>At least one number (0-9)</li>
              </ul>
            </div>

            <button
              type="submit"
              className="btn primary submit-btn"
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        )}

        {statusMessage?.type === 'success' && (
          <div className="success-actions">
            <p className="success-reminder">Go back to the Fluffer App to sign in with your new password.</p>
          </div>
        )}
      </div>

      <footer className="reset-footer">
        <p>&copy; {new Date().getFullYear()} Fluffer. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default ResetPassword;
